import React, { useEffect, useState } from "react";
import Button, {
  ButtonTypes,
  ButtonColorTypes,
} from "../../shared-components/Button";
import InputField from "../../shared-components/InputField";
import { uniqueUUID } from "../../utils";
import { CompanyEditHeaders } from "../../api/Companies";

export interface CallbackHeadersDefinitions {
  field_key: string;
  key: string;
  value: string;
}

type Props = {
  onChange?: (aliases: CallbackHeadersDefinitions[]) => void;
  label?: string;
  labelClassName?: string;
  alternateColor?: boolean;
  prefill?: CompanyEditHeaders;
  readOnly?: boolean;
};

function buildEmptyDefinition(): CallbackHeadersDefinitions {
  return {
    key: "",
    value: "",
    field_key: uniqueUUID(),
  };
}

const CompanyCallbackHeaders = ({
  onChange,
  alternateColor,
  prefill,
  label,
  labelClassName,
  readOnly = false,
}: Props) => {
  const [fieldsList, setFields] = useState<CallbackHeadersDefinitions[]>([]);

  const appendFields = () => {
    handleChange([...fieldsList, buildEmptyDefinition()]);
  };

  const updateField = (idx: number, field: "key" | "value", value: string) => {
    fieldsList[idx][field] = value;
    handleChange([...fieldsList]);
  };

  const removeField = (idx: number) => {
    fieldsList.splice(idx, 1);
    handleChange([...fieldsList]);
  };

  const handleChange = (list: CallbackHeadersDefinitions[]) => {
    onChange?.(list);
    setFields(list);
  };

  const handleAdd = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    appendFields();
  };

  const handleRemove = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    idx: number
  ) => {
    e.preventDefault();
    removeField(idx);
  };
  const buildCallbackHeadersAsPrefill = (stateHeaders: CompanyEditHeaders) => {
    let header_keys: string[] = Object.keys(stateHeaders ?? {});
    let header_values: string[] = Object.values(stateHeaders ?? {});
    let callbackHeaders: CallbackHeadersDefinitions[] = [];
    for (let index = 0; index < header_keys.length; index++) {
      const key: string = header_keys[index];
      const value: string = header_values[index];
      callbackHeaders.push({ key: key, value: value, field_key: uniqueUUID() });
    }
    return callbackHeaders;
  };
  useEffect(() => {
    if (prefill && Object.keys(prefill).length >= 1) {
      let callbackHeaders = buildCallbackHeadersAsPrefill(prefill);
      setFields([...callbackHeaders]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  return (
    <>
      {label && (
        <label className={"block text-sm font-medium text-gray-600 mb-2"}>
          {label}
        </label>
      )}
      {fieldsList.map((fields, idx) => (
        <div
          key={fields.field_key}
          className="flex flex-wrap my-4 w-full items-end"
        >
          <InputField
            handleChange={(e) => {
              updateField(idx, "key", e.target.value);
            }}
            name={`key-${idx}`}
            value={fieldsList[idx].key}
            placeholder=""
            label={"Key"}
            labelClassName={labelClassName}
            classes="mb-4 mr-8 flex-1"
            disabled={readOnly}
          />
          <InputField
            handleChange={(e) => {
              updateField(idx, "value", e.target.value);
            }}
            name={`value-${idx}`}
            placeholder=""
            value={fieldsList[idx].value}
            label={"Value"}
            labelClassName={labelClassName}
            classes="mb-4 flex-1"
            disabled={readOnly}
          />
          {!readOnly && idx >= 0 && (
            <div className="flex-0 p-4">
              <Button
                type={ButtonTypes.remove}
                text=""
                colorType={ButtonColorTypes.custom}
                onClick={(e) => handleRemove(e, idx)}
                className="inline"
              />
            </div>
          )}
        </div>
      ))}
      {!readOnly && (
        <Button
          type={ButtonTypes.add}
          text="Add Header"
          colorType={
            alternateColor
              ? ButtonColorTypes.alternate
              : ButtonColorTypes.action
          }
          onClick={handleAdd}
        />
      )}
    </>
  );
};

export default CompanyCallbackHeaders;
